import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

export interface LinkStyleParams {
  sidebarOpen: boolean;
  active: boolean;
  hasChildren: boolean;
  theme: Theme;
  isPublisher?: boolean;
}

export interface TextStyleParams {
  sidebarOpen: boolean;
  active: boolean;
  theme: Theme;
}

export interface CaretStyleParams {
  level: number;
  isCollapseActive: boolean;
}

export interface MobileCaretStyleParams {
  isCollapseActive: boolean;
}

// using SystemStyleObject for styles that will be used in nested selectors
type StyleObject = SystemStyleObject<Theme>;

// Active background and color for top-level parent items with children
const getActiveBackgroundColor = (hasChildren: boolean) => {
  if (hasChildren) {
    return { backgroundColor: 'secondary.main', color: 'dianomiGreen.main' };
  }
  return {};
};

export const getMobileLinkStyle = (): StyleObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 1.5,
  px: 2,
  color: 'text.primary',
  backgroundColor: 'transparent',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
});

export const getMobileNestedLinkStyle = (): StyleObject => ({
  ...getMobileLinkStyle(),
  pl: '12px',
});

export const getMobileTextStyle = (params: { level: number; active: boolean; theme: Theme }) => ({
  fontSize: params.level === 0 ? '14px' : '13px',
  fontWeight: params.active ? 700 : 500,
  textDecoration: params.active ? 'underline' : 'none',
  color: params.active ? params.theme.palette.dianomiGreen.main : 'inherit',
});

export const getMobileCaretStyle = (params: MobileCaretStyleParams): StyleObject => ({
  display: 'inline-block',
  width: 0,
  height: 0,
  marginLeft: 'auto',
  borderTop: '5px solid',
  borderRight: '5px solid transparent',
  borderLeft: '5px solid transparent',
  transition: 'transform 150ms ease-in',
  ...(params.isCollapseActive && { transform: 'rotate(180deg)' }),
});

const getPaddingLeft = (sidebarOpen: boolean): string => {
  if (!sidebarOpen) return '30px !important';
  return '20px !important';
};

export const getBaseLinkStyles = (params: LinkStyleParams): StyleObject => {
  return {
    transition: 'all 300ms linear',
    position: 'relative',
    display: 'block',
    backgroundColor: params.theme.palette.dianomiGrey.main,
    color: params.theme.palette?.primary.main,
    width: 'auto',
    cursor: 'pointer',
    margin: '4px 0px 0px',
    '&:hover': {
      outline: 'none',
      backgroundColor: params.theme.palette?.secondary.main,
      boxShadow: 'none',
      color: params.theme.palette?.dianomiGreen.main,
    },
    '&.active': {
      outline: 'none',
      backgroundColor: 'secondary.main',
      boxShadow: 'none',
      color: params.theme.palette?.dianomiGreen.main,
    },
    // Active state - green border indicates current product area/scope
    ...(params.active && {
      ...getActiveBackgroundColor(params.hasChildren),
      borderRight: `solid 4px ${params.theme.palette?.dianomiGreen.main}`,
    }),
    paddingLeft: getPaddingLeft(params.sidebarOpen),
  };
};

// desktop item link styles with icons

export const getItemLinkStyles = (params: LinkStyleParams): StyleObject => ({
  ...getBaseLinkStyles(params),
  padding: '10px',
  borderRadius: 0,
  textDecoration: 'none',
  // Only show focus indicator on keyboard navigation, not mouse clicks
  '&:focus-visible': {
    textDecoration: 'underline',
  },
  '&:focus:not(:focus-visible)': {
    textDecoration: 'none',
  },
  [params.theme.breakpoints?.down('md')]: {
    paddingBottom: '10px',
  },
});

// desktop collapse item link styles (no icon - child links)
// Visual design: grey hover background, fontWeight 800 when active

export const getCollapseItemLinkStyles = (params: LinkStyleParams): StyleObject => {
  return {
    transition: 'all 150ms ease',
    position: 'relative',
    display: 'block',
    backgroundColor: 'transparent',
    color: params.theme.palette?.primary.main,
    width: 'auto',
    cursor: 'pointer',
    margin: '0px !important',
    borderRadius: '0px !important',
    border: 'none',
    // indent for hierarchy - leave room for the rail
    paddingLeft: '16px !important',
    '&:hover': {
      outline: 'none',
      backgroundColor: '#dedede',
      boxShadow: 'none',
      border: 'none',
      textDecoration: 'none',
    },
  };
};

// desktop item text styles with icons

export const getItemTextStyles = (params: TextStyleParams): StyleObject => {
  return {
    margin: 0,
    lineHeight: '30px',
    fontSize: { sm: '12px', xl: '13px' },
    transform: params.sidebarOpen ? 'translate3d(0px, 0, 0)' : 'translate3d(-25px, 0, 0)',
    opacity: params.sidebarOpen ? 1 : 0,
    transition: 'transform 300ms ease 0s, opacity 300ms ease 0s',
    position: 'relative',
    display: 'block',
    height: 'auto',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    width: '100%',
    color: 'inherit',
    '&:focus, &.active': {
      borderBottom: 'none',
      textDecoration: 'none',
      fontWeight: 800,
    },
    ...(params.active && {
      fontWeight: 800,
    }),
    [params.theme.breakpoints?.down('md')]: {
      borderBottom: 'none',
      paddingBottom: 0,
    },
  };
};

// desktop collapse item text styles (no icon - child links)
// Active items get fontWeight 800 - consistent across all modes

export const getCollapseItemTextStyles = (params: TextStyleParams): StyleObject => {
  return {
    margin: 0,
    position: 'relative',
    transform: 'translateX(0px)',
    opacity: 1,
    whiteSpace: 'normal',
    display: 'block',
    transition: 'all 150ms ease 0s',
    fontSize: '12px',
    fontWeight: params.active ? 800 : 500,
    color: 'inherit',
    padding: '8px 10px 8px 0px',
    lineHeight: '20px',
    maxWidth: params.theme?.defaultStyles?.drawerWidth,
    border: 'none',
    '&:focus': {
      border: 'none',
    },
  };
};

// desktop caret styles

export const getCaretStyles = (params: CaretStyleParams): StyleObject => {
  const baseCaret: StyleObject = {
    marginTop: '13px',
    position: 'absolute',
    transition: 'all 150ms ease-in',
    display: 'inline-block',
    width: 0,
    height: 0,
    marginLeft: '2px',
    verticalAlign: 'middle',
    borderTop: '4px solid',
    borderRight: '4px solid transparent',
    borderLeft: '4px solid transparent',
  };

  if (params.level === 0 || params.level === 1) {
    return {
      ...baseCaret,
      left: 'auto',
      right: '18px',
      ...(params.isCollapseActive && {
        transform: 'rotate(180deg)',
      }),
    };
  }

  return {
    ...baseCaret,
    left: '-20px',
    ...(params.isCollapseActive && {
      transform: 'rotate(180deg)',
    }),
  };
};

// list item container style

export interface ListItemStyleParams {
  isMobile: boolean;
  isChild?: boolean;
  theme?: Theme;
}

export const getListItemStyle = (isMobileOrParams: boolean | ListItemStyleParams): StyleObject => {
  // Support both old signature (boolean) and new signature (object)
  const params: ListItemStyleParams =
    typeof isMobileOrParams === 'boolean'
      ? { isMobile: isMobileOrParams, isChild: false }
      : isMobileOrParams;

  const { isMobile, isChild } = params;

  // Determine background color based on context
  const getBackgroundColor = () => {
    if (isMobile) return 'transparent';
    if (isChild) {
      return 'transparent';
    }
    return params?.theme?.palette?.dianomiGrey.main;
  };

  return {
    position: 'relative',
    display: 'block',
    textDecoration: 'none',
    margin: '0px',
    padding: 0,
    paddingLeft: isMobile && !isChild ? '2px' : 0,
    backgroundColor: getBackgroundColor(),
  };
};

export const getCollapseListSx = (): StyleObject => ({
  position: 'relative',
  paddingLeft: '12px',
  marginLeft: '20px',
  // thin vertical rail for hierarchy
  borderLeft: '1px solid rgba(0, 0, 0, 0.15)',
  '& .MuiListItem-root': {
    backgroundColor: 'transparent',
  },
});
